import React, { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { Button } from '../components/new-components/ui/button';
import { authApi } from '../services/api';
import { BRAND_ASSETS, TENANT } from '../config';
import { InlineError } from '../components/Auth/InlineError';

const passwordHelp = 'Use at least 8 characters with uppercase, lowercase, a number, and a special character.';

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const passwordValid = useMemo(
    () => /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&].{7,}$/.test(password),
    [password],
  );

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');

    if (!token) {
      setError('This password reset link is missing a token.');
      return;
    }
    if (!passwordValid) {
      setError(passwordHelp);
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await authApi.resetPassword({ token, password, confirmPassword });
      if (response.success) {
        setSuccess(true);
      } else {
        setError(response.message || 'Unable to reset password.');
      }
    } finally {
      setLoading(false);
    }
  };

  const inputClass = `w-full px-4 py-4 text-lg rounded-xl border-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none transition-colors ${
    error ? 'border-destructive focus:border-destructive' : 'border-border focus:border-primary'
  }`;

  return (
    <div className="min-h-screen bg-background flex flex-col portal-typography">
      {/* Header — back + logo, matching AuthFlow */}
      <div className="flex items-center justify-between px-4 md:px-6 py-4 md:py-6">
        <Link
          to="/login"
          aria-label="Back to sign in"
          className="flex items-center gap-2 text-foreground/70 hover:text-foreground transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <Link to="/" className="flex items-center gap-2 hover:opacity-80 transition-opacity">
          <img src={BRAND_ASSETS.logo.primary} alt={TENANT.branding.name} className="h-11 object-contain" />
        </Link>
        <div className="w-8" />
      </div>

      {/* Main content */}
      <div className="flex-1 flex items-center justify-center px-6 pb-16">
        <div className="w-full max-w-md">
          {success ? (
            <div className="text-center space-y-6">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <div className="space-y-2">
                <h1 className="text-3xl md:text-4xl font-medium">Password updated</h1>
                <p className="text-lg text-muted-foreground">You can now sign in with your new password.</p>
              </div>
              <Button className="w-full h-14 text-lg" onClick={() => navigate('/login')}>
                Return to sign in
              </Button>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="space-y-4">
                <h1 className="text-4xl md:text-5xl font-medium">Create a new password</h1>
                <p className="text-lg text-muted-foreground">{passwordHelp}</p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-4">
                  {/* New password */}
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => { setPassword(event.target.value); if (error) setError(''); }}
                      placeholder="New password"
                      aria-label="New password"
                      className={`${inputClass} !pr-20`}
                      autoComplete="new-password"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((value) => !value)}
                      tabIndex={-1}
                      className="absolute right-12 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>

                  {/* Confirm password */}
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(event) => { setConfirmPassword(event.target.value); if (error) setError(''); }}
                    placeholder="Confirm password"
                    aria-label="Confirm password"
                    className={inputClass}
                    autoComplete="new-password"
                  />
                </div>

                <InlineError className="mt-1.5 text-sm">{error}</InlineError>

                <Button type="submit" disabled={loading || !token} className="w-full h-14 text-lg">
                  {loading ? 'Updating...' : 'Reset password'}
                </Button>
              </form>

              <div className="text-center">
                <Link to="/login" className="font-semibold text-foreground underline">
                  Back to sign in
                </Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
